import * as RadixContextMenu from '@radix-ui/react-context-menu';
import classNames from 'classnames';
import { memo, type ReactNode } from 'react';

import { ChevronRightIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type ContextMenuItemProps, contextMenuItem } from './ContextMenuItem';

export type ContextMenuSubProps = Pick<RadixContextMenu.ContextMenuSubProps, 'defaultOpen' | 'onOpenChange' | 'open'> &
  Omit<RadixContextMenu.ContextMenuSubContentProps, 'prefix'> &
  Pick<RadixContextMenu.ContextMenuSubTriggerProps, 'disabled'> &
  Pick<ContextMenuItemProps, 'description' | 'label' | 'prefix' | 'suffix'> & {
    'data-id'?: string;
    description?: ReactNode;
  };

export const ContextMenuSub = memo(
  ({
    children,
    className,
    defaultOpen,
    description,
    disabled,
    label,
    onOpenChange,
    open,
    prefix,
    suffix,
    'data-id': dataId,
    ...restOfProps
  }: ContextMenuSubProps) => {
    const hasDescription = !!description;

    return (
      <RadixContextMenu.Sub defaultOpen={defaultOpen} onOpenChange={onOpenChange} open={open}>
        <RadixContextMenu.SubTrigger
          className={contextMenuItem({ description: hasDescription })}
          data-id={dataId}
          data-testid="CONTEXT_MENU_SUB_TRIGGER"
          disabled={disabled}
        >
          {prefix ? (
            <div className={classNames('mr-2 flex shrink-0 items-center gap-1', hasDescription ? 'mt-0.5' : 'mt-0')}>
              {prefix}
            </div>
          ) : null}

          <div className="flex min-w-0 grow flex-col">
            <span className="truncate text-14">{label}</span>
            {hasDescription ? <span className="ads:text-metadata text-12 text-gray-10">{description}</span> : null}
          </div>

          <div className={classNames('ml-2 flex shrink-0 items-center gap-1', hasDescription ? 'mt-0.5' : 'mt-0')}>
            {suffix}
            <ChevronRightIcon size="sm" />
          </div>
        </RadixContextMenu.SubTrigger>

        <RadixContextMenu.Portal>
          <RadixContextMenu.SubContent
            className={cn(
              'z-portal flex max-h-[var(--radix-context-menu-content-available-height)] w-[216px] min-w-[var(--radix-popper-anchor-width)] radix-side-left:animate-slide-down radix-side-right:animate-slide-up flex-col gap-2 overflow-y-auto rounded-4 ads:border-default ads:bg-raised bg-surface-1 p-2 ads:text-primary text-gray-12 shadow-popover outline-none dark:border dark:border-gray-5',
              className,
            )}
            data-testid="CONTEXT_MENU_SUB"
            {...restOfProps}
          >
            {children}
          </RadixContextMenu.SubContent>
        </RadixContextMenu.Portal>
      </RadixContextMenu.Sub>
    );
  },
);

ContextMenuSub.displayName = 'ContextMenuSub';
